import { useState } from 'react'
import InputFormCard from '@component/InputFormCard'
import UnitAdornment from '@component/UnitAdornment'
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { useHistoryList } from '@component/HistoryList'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { USAGE } from '@core/const'
import { Query } from '@core/query'
import { useColumn } from './context'
import { makeFormRules } from '@/utils'

function ColumnForm () {
  const { t } = useTranslation()

  const { form, setResult } = useColumn()
  const { appendHistory } = useHistoryList()

  const {
    control,
    reset,
    handleSubmit,
  } = form

  const rules = makeFormRules()

  const [isLoading, setLoading] = useState(false)
  const onSubmit = handleSubmit(async _ => {
    if (_.concreteStrength <= 0 || _.longSideSpan <= 0 || _.shortSideSpan <= 0
      || _.slabThickness <= 0 || _.stories <= 0 || _.criterionSize <= 0) {
      alert(t('SENTENCE.CHECK_YOUR_INPUT'))
      return
    }

    setLoading(true)
    const res = await Query.requestCalcColumn(_)
    setLoading(false)

    setResult(res)
    appendHistory({
      type: 'Column',
      ..._,
      result: res,
    })
  })

  const onReset = () => {
    reset()
    setResult(null)
  }

  return (
    <InputFormCard
      title={t('GLOSSARY.COLUMN')}
      onReset={onReset}
      onSubmit={onSubmit}
      loading={isLoading}
    >
      <Controller
        name="longSideSpan"
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <TextField
            label={t('GLOSSARY.LONGSIDE_SPAN')}
            InputProps={{
              endAdornment: <UnitAdornment unit="m" />,
            }}
            {...field}
            error={fieldState.invalid}
          />
        )}
      />
      <Controller
        name="shortSideSpan"
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <TextField
            label={t('GLOSSARY.SHORTSIDE_SPAN')}
            InputProps={{
              endAdornment: <UnitAdornment unit="m" />,
            }}
            {...field}
            error={fieldState.invalid}
          />
        )}
      />
      <FormControl variant="standard">
        <InputLabel>
          {t('WORD.USAGE')}
        </InputLabel>
        <Controller
          control={control}
          name="usage"
          render={({ field }) => (
            <Select {...field}>
              {Object.keys(USAGE).map(_ => (
                <MenuItem key={_} value={_}>
                  {t(`GLOSSARY.USAGE.${_}`)}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </FormControl>
      <Controller
        name="slabThickness"
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <TextField
            label={t('GLOSSARY.SLAB_THICKNESS')}
            InputProps={{
              endAdornment: <UnitAdornment unit="mm" />,
            }}
            {...field}
            error={fieldState.invalid}
          />
        )}
      />
      <Controller
        name="stories"
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <TextField
            label={t('WORD.STORIES')}
            {...field}
            error={fieldState.invalid}
          />
        )}
      />
      <Controller
        name="concreteStrength"
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <TextField
            label={t('GLOSSARY.CONCRETE_STRENGTH')}
            InputProps={{
              endAdornment: <UnitAdornment unit="MPa" />,
            }}
            {...field}
            error={fieldState.invalid}
          />
        )}
      />
      <Controller
        name="criterionSize"
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <TextField
            label={t('GLOSSARY.CRITERION_SIZE')}
            InputProps={{
              endAdornment: <UnitAdornment unit="mm" />,
            }}
            {...field}
            error={fieldState.invalid}
          />
        )}
      />
    </InputFormCard>
  )
}

export default ColumnForm
